// Self-service Seat-Referral attribution card.
// Surfaces on /app/billing for any user who has invited teammates that later
// subscribed to a paid plan on their own credit card.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, TrendingUp, Loader2, Sparkles } from "lucide-react";

export default function MyReferralsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/billing/my-referrals")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card p-5 mb-5" data-testid="my-referrals-card-loading">
        <div className="text-sm text-[var(--ink-2)]">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Loading your referrals…
        </div>
      </div>
    );
  }

  // Render the empty-state card too so users know the feature exists & how to earn credit.
  const isEmpty = !data || data.count === 0;
  return (
    <div className="card p-5 mb-5" data-testid="my-referrals-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
            <Users size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-extrabold text-lg text-[var(--ink)] flex items-center gap-2">
              Your Referrals
              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider">
                Viral
              </span>
            </h3>
            <p className="text-xs text-[var(--ink-2)] mt-0.5">
              When you invite teammates to your workspace, you get credit for every
              upgrade they make — even after they leave your team.
            </p>
          </div>
        </div>
      </div>

      {isEmpty ? (
        <div className="rounded-lg border border-dashed border-[var(--line)] p-5 text-center bg-slate-50/50" data-testid="my-referrals-empty">
          <Sparkles className="mx-auto text-emerald-600 mb-2" size={20} />
          <div className="text-sm font-bold text-[var(--ink)] mb-1">No referral credits yet</div>
          <p className="text-xs text-[var(--ink-3)] leading-snug max-w-md mx-auto">
            Invite a friend to your Teams workspace via <strong>Settings → Team</strong>.
            If they later upgrade to Pro, Hustler, or Teams on their own card, you'll
            earn referral credit here.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-lg border border-[var(--line)] p-3">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--ink-3)]">Referrals converted</div>
              <div className="font-display font-extrabold text-2xl tabular-nums text-[var(--ink)]" data-testid="my-referrals-count">
                {data.count}
              </div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 flex items-center gap-1">
                <TrendingUp size={11} /> MRR you've driven
              </div>
              <div className="font-display font-extrabold text-2xl tabular-nums text-emerald-700" data-testid="my-referrals-mrr">
                ${data.total_mrr_attributed_usd}<span className="text-xs font-normal text-emerald-600">/mo</span>
              </div>
            </div>
          </div>

          <div className="border border-[var(--line)] rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="my-referrals-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-[var(--ink-3)]">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Teammate</th>
                  <th className="text-left px-3 py-2 font-semibold">Tier</th>
                  <th className="text-right px-3 py-2 font-semibold">MRR</th>
                  <th className="text-right px-3 py-2 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {data.attributions.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-[var(--ink)]">
                      <div className="font-semibold">{a.invitee_name || a.invitee_email.split("@")[0]}</div>
                      <div className="text-[10px] text-[var(--ink-3)] truncate max-w-[180px]">{a.invitee_email}</div>
                    </td>
                    <td className="px-3 py-2 capitalize">{a.upgrade_tier}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">
                      ${a.mrr_attributed_usd}/mo
                    </td>
                    <td className="px-3 py-2 text-right text-[11px] text-[var(--ink-3)]">
                      {a.attributed_at ? new Date(a.attributed_at).toLocaleDateString() : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
